import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/AllNeedReviewMarketplacePosts.graphql';
import { Query, QueryallNeedReviewMarketplacePostsArgs } from '../../__generated__/globalTypes';

export const useAllNeedReviewMarketplacePostsQuery = (
  options?: QueryHookOptions<Query, QueryallNeedReviewMarketplacePostsArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryallNeedReviewMarketplacePostsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useAllNeedReviewMarketplacePostsLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryallNeedReviewMarketplacePostsArgs>
): QueryResult<Query, QueryallNeedReviewMarketplacePostsArgs> & {
  getAllNeedReviewMarketplacePosts: LazyQueryExecFunction<Query, QueryallNeedReviewMarketplacePostsArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getAllNeedReviewMarketplacePosts, result] = useLazyQuery<Query, QueryallNeedReviewMarketplacePostsArgs>(
    QUERY,
    {
      onError: (error) => {
        enqueueSnackbar(t(error.message), { variant: 'error' });
      },
      ...options
    }
  );

  return { getAllNeedReviewMarketplacePosts, ...result };
};

export const useAllNeedReviewMarketplacePostsPromiseQuery = () => {
  const client = useApolloClient();

  const getAllNeedReviewMarketplacePosts = useCallback(
    (options?: Omit<QueryOptions<QueryallNeedReviewMarketplacePostsArgs, Query>, 'query'>) =>
      client.query<Query, QueryallNeedReviewMarketplacePostsArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getAllNeedReviewMarketplacePosts };
};
// Suspense Query Hooks
export const useAllNeedReviewMarketplacePostsSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryallNeedReviewMarketplacePostsArgs>
) => {
  const result = useSuspenseQuery<Query, QueryallNeedReviewMarketplacePostsArgs>(QUERY, {
    ...options
  });

  return result;
};
